import { fecha, franja, type Autoria } from '../lib/formato';
import type { Estado, Rol } from '../lib/tipos';

type Fase = 'pendiente' | 'turno' | 'hecho' | 'parado';

interface Tramo {
  rol: string;
  estado: string;
  fase: Fase;
}

/**
 * El pase de una cocina: la solicitud pasa de mano en mano y en cada momento
 * el testigo está en una sola. De un vistazo se ve a quién le toca.
 *
 * M-04 · Cuando la solicitud se cierra, el aviso de «Cancelada» o «Rechazada»
 * se coloca en el tramo de quien realmente ejecutó la acción, tomado del
 * histórico. Antes se daba por hecho que había sido el Jefe de Ventas, y por
 * eso una cancelación del Comercial aparecía atribuida a otra persona.
 */
export function calcularTramos({
  estado,
  planCocina,
  enviada,
  fechaFinal,
  fechaFin,
  autoria,
}: {
  estado: Estado;
  planCocina: 'PENDING' | 'CONFIRMED';
  enviada: string | null;
  fechaFinal: string | null;
  fechaFin: string | null;
  /** Autoría del estado actual, derivada del histórico. */
  autoria?: Autoria | null;
}) {
  const cerrada = ['CANCELLED','REJECTED','CLOSED','NOT_PERFORMED'].includes(estado);
  const cierre = cerrada ? (autoria ?? null) : null;
  const etiquetaCierre = estado === 'CANCELLED' ? 'Cancelada' : estado==='CLOSED'?'Cerrada':estado==='NOT_PERFORMED'?'No realizada':'Rechazada';

  /** ¿En qué carril vive quien cerró el expediente? */
  const carrilDe = (rol: Rol | null | undefined): 0 | 1 | 2 | null => {
    switch (rol) {
      case 'SALESPERSON':
        return 0;
      case 'SALES_MANAGER':
        return 1;
      case 'COOK':
        return 2;
      default:
        return null; // Admin o sistema: no pertenece a ningún carril
    }
  };
  const carrilCierre = cierre ? carrilDe(cierre.rol) : null;

  const comercial: Tramo =
    estado === 'DRAFT'
      ? { rol: 'Comercial', estado: 'Preparando la solicitud', fase: 'turno' }
      : { rol: 'Comercial', estado: enviada ? `Enviada el ${fecha(enviada)}` : 'Enviada', fase: 'hecho' };

  let jefe: Tramo;
  if (estado === 'DRAFT') jefe = { rol: 'Jefe de Ventas', estado: 'A la espera', fase: 'pendiente' };
  else if (estado === 'SENT') jefe = { rol: 'Jefe de Ventas', estado: 'Sin abrir', fase: 'turno' };
  else if (estado === 'RECEIVED')
    jefe = { rol: 'Jefe de Ventas', estado: 'Pendiente de valorar', fase: 'turno' };
  else if (cerrada)
    jefe = {
      rol: 'Jefe de Ventas',
      estado: fechaFinal ? franja(fechaFinal, fechaFin) : 'Revisada',
      fase: fechaFinal ? 'hecho' : 'pendiente',
    };
  else
    jefe = {
      rol: 'Jefe de Ventas',
      estado: fechaFinal ? franja(fechaFinal, fechaFin) : 'Autorizada',
      fase: 'hecho',
    };

  let cocina: Tramo;
  if (estado === 'CLOSED') cocina = { rol: 'Cocina', estado: 'Visita realizada', fase: 'hecho' };
  else if (estado === 'NOT_PERFORMED') cocina = { rol: 'Cocina', estado: 'No realizada', fase: 'parado' };
  else if (estado === 'CANCELLED' || estado === 'REJECTED')
    cocina = { rol: 'Cocina', estado: 'Sin efecto', fase: 'pendiente' };
  else if (estado !== 'AUTHORIZED')
    cocina = { rol: 'Cocina', estado: 'A la espera', fase: 'pendiente' };
  else if (planCocina === 'CONFIRMED')
    cocina = { rol: 'Cocina', estado: 'Plan confirmado', fase: 'hecho' };
  else cocina = { rol: 'Cocina', estado: 'Pendiente de plan', fase: 'turno' };

  if(estado==='PENDING_REVIEW'||estado==='PENDING_FOLLOWUP') {
    comercial.estado=estado==='PENDING_REVIEW'?'Realización y valoración pendientes':'Seguimiento del pedido';
    comercial.fase='turno'; cocina={rol:'Cocina',estado:'Visita finalizada',fase:'hecho'};
  }
  const tramos: Tramo[] = [comercial, jefe, cocina];

  // El carril de quien cerró se marca como parado y dice su nombre.
  if (cierre && carrilCierre !== null &&
      (carrilCierre !== 2 || estado === 'CANCELLED' || estado === 'REJECTED')) {
    tramos[carrilCierre] = {
      rol: tramos[carrilCierre]!.rol,
      estado: `${etiquetaCierre} · ${cierre.nombre}`,
      fase: 'parado',
    };
  }

  return { tramos, cierre, carrilCierre, etiquetaCierre };
}

export function Pase(props: Parameters<typeof calcularTramos>[0]) {
  const { tramos, cierre, carrilCierre, etiquetaCierre } = calcularTramos(props);
  return (
    <div className="pila" style={{ gap: 'var(--r2)' }}>
      <div className="pase" aria-label="Situación de la solicitud">
        {tramos.map((t) => (
          <div key={t.rol} className="pase-tramo" data-fase={t.fase}>
            <span className="rol">{t.rol}</span>
            <span className="estado" title={t.estado}>
              {t.estado}
            </span>
          </div>
        ))}
      </div>

      {/* Admin y sistema no tienen carril propio: se dicen aparte, sin
          atribuir la acción a ninguno de los tres perfiles operativos. */}
      {cierre && carrilCierre === null && (
        <p className="sutil" style={{ margin: 0 }}>
          {etiquetaCierre} por {cierre.nombre} ({cierre.rolEtiqueta}).
        </p>
      )}
    </div>
  );
}

/** Frase corta para encabezados y listados: «Le toca al Jefe de Ventas». */
export function turnoDe(
  estado: Estado,
  planCocina: 'PENDING' | 'CONFIRMED',
  autoria?: Autoria | null,
) {
  switch (estado) {
    case 'PENDING_REVIEW': return 'Valoración pendiente del Comercial';
    case 'PENDING_FOLLOWUP': return 'Seguimiento del pedido';
    case 'CLOSED': return autoria ? `Cerrada por ${autoria.rolEtiqueta.toLowerCase()}` : 'Cerrada';
    case 'NOT_PERFORMED': return autoria ? `No realizada · respuesta de ${autoria.rolEtiqueta.toLowerCase()}` : 'No realizada';
    case 'DRAFT':
      return 'Borrador del Comercial';
    case 'SENT':
    case 'RECEIVED':
      return 'Le toca al Jefe de Ventas';
    case 'AUTHORIZED':
      return planCocina === 'CONFIRMED' ? 'Visita cerrada y comunicada' : 'Le toca al Cocinero';
    case 'REJECTED':
      return autoria ? `Rechazada por ${autoria.rolEtiqueta.toLowerCase()}` : 'Rechazada';
    case 'CANCELLED':
      return autoria ? `Cancelada por ${autoria.rolEtiqueta.toLowerCase()}` : 'Cancelada';
  }
}
